import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Calendar, Tag, Share2, ChevronRight } from "lucide-react";
import { NEWS_FULL } from "@/data/newsData";

export default function NotiziaDettaglioPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const notizia = NEWS_FULL.find((n) => n.slug === slug);

  if (!notizia) {
    return (
      <main id="main-content" className="py-20 text-center" data-testid="notizia-not-found">
        <div className="max-w-7xl mx-auto px-4">
          <h1 className="text-2xl font-bold text-[var(--confcom-text-primary)] font-['Encode_Sans_Semi_Condensed'] mb-4">Notizia non trovata</h1>
          <Link to="/notizie" className="text-[var(--confcom-blue)] font-medium hover:underline">Torna alle notizie</Link>
        </div>
      </main>
    );
  }

  const correlati = (notizia.correlati || [])
    .map((id) => NEWS_FULL.find((n) => n.id === id))
    .filter(Boolean);

  return (
    <main id="main-content" data-testid="notizia-dettaglio-page">
      {/* Hero image */}
      <section className="relative h-[320px] md:h-[420px] overflow-hidden">
        <img
          src={notizia.image}
          alt={notizia.titolo}
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#041E42]/80 via-[#041E42]/40 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 z-10">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-8">
            <button
              onClick={() => navigate(-1)}
              className="inline-flex items-center gap-2 text-sm text-white/80 hover:text-white mb-4 transition-colors"
              data-testid="notizia-back"
            >
              <ArrowLeft size={16} aria-hidden="true" /> Torna indietro
            </button>
            <div className="flex flex-wrap items-center gap-3 mb-3">
              {notizia.categorie.map((cat) => (
                <span key={cat} className="text-xs font-semibold text-white bg-white/20 backdrop-blur-sm px-3 py-1 rounded-md">
                  {cat}
                </span>
              ))}
            </div>
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white font-['Encode_Sans_Semi_Condensed'] tracking-tight leading-tight" data-testid="notizia-titolo">
              {notizia.titolo}
            </h1>
          </div>
        </div>
      </section>

      {/* Article content */}
      <section className="py-12 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Meta */}
          <div className="flex flex-wrap items-center gap-4 mb-8 pb-8 border-b border-[var(--confcom-border)]">
            <div className="flex items-center gap-2 text-sm text-[var(--confcom-text-secondary)]">
              <Calendar size={16} className="text-[var(--confcom-blue)]" aria-hidden="true" />
              <time dateTime={notizia.data}>{notizia.data}</time>
            </div>
            <div className="flex items-center gap-2 text-sm text-[var(--confcom-text-secondary)]">
              <Tag size={16} className="text-[var(--confcom-blue)]" aria-hidden="true" />
              <span>{notizia.categorie.join(", ")}</span>
            </div>
          </div>

          {/* Body */}
          <article
            className="prose prose-lg max-w-none
              prose-headings:font-['Encode_Sans_Semi_Condensed'] prose-headings:text-[var(--confcom-text-primary)] prose-headings:font-semibold
              prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-4
              prose-p:text-[var(--confcom-text-secondary)] prose-p:leading-relaxed prose-p:mb-4
              prose-strong:text-[var(--confcom-text-primary)]
              prose-ul:text-[var(--confcom-text-secondary)] prose-ul:space-y-2
              prose-li:text-[var(--confcom-text-secondary)]
              prose-a:text-[var(--confcom-blue)] prose-a:font-medium prose-a:no-underline hover:prose-a:underline"
            dangerouslySetInnerHTML={{ __html: notizia.contenuto }}
            data-testid="notizia-contenuto"
          />

          {/* Share */}
          <div className="mt-12 pt-8 border-t border-[var(--confcom-border)] flex items-center justify-between">
            <Link
              to="/notizie"
              className="inline-flex items-center gap-2 text-sm font-medium text-[var(--confcom-blue)] hover:gap-3 transition-all"
              data-testid="notizia-back-list"
            >
              <ArrowLeft size={16} aria-hidden="true" /> Tutte le notizie
            </Link>
            <button
              onClick={() => navigator.clipboard?.writeText(window.location.href)}
              className="inline-flex items-center gap-2 text-sm text-[var(--confcom-text-muted)] hover:text-[var(--confcom-blue)] transition-colors"
              data-testid="notizia-share"
            >
              <Share2 size={16} aria-hidden="true" /> Condividi
            </button>
          </div>
        </div>
      </section>

      {/* Related articles */}
      {correlati.length > 0 && (
        <section className="py-12 bg-[var(--confcom-bg-subtle)]" aria-labelledby="correlati-title">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 id="correlati-title" className="text-2xl font-bold text-[var(--confcom-text-primary)] font-['Encode_Sans_Semi_Condensed'] mb-8">
              Potrebbe interessarti anche
            </h2>
            <div className="grid md:grid-cols-3 gap-6">
              {correlati.map((n) => (
                <Link
                  key={n.id}
                  to={`/notizie/${n.slug}`}
                  className="group bg-white rounded-xl border border-[var(--confcom-border)] overflow-hidden hover:shadow-lg transition-all"
                  data-testid={`correlato-${n.id}`}
                >
                  <div className="h-44 overflow-hidden">
                    <img
                      src={n.image}
                      alt={n.titolo}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                  </div>
                  <div className="p-5">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-xs font-medium text-[var(--confcom-blue)] bg-[var(--confcom-bg-blue-light)] px-2 py-0.5 rounded">{n.categoria}</span>
                      <time className="text-xs text-[var(--confcom-text-muted)]">{n.data}</time>
                    </div>
                    <h3 className="text-sm font-semibold text-[var(--confcom-text-primary)] font-['Encode_Sans_Semi_Condensed'] leading-snug group-hover:text-[var(--confcom-blue)] transition-colors line-clamp-2">
                      {n.titolo}
                    </h3>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </main>
  );
}
